/**
 * 定型文マスタ (`GET /phrase-templates`) の既定文面を配る軽量コンテキスト
 * (Issue #552)。
 *
 * ## 何の不具合を直すためのものか
 *
 * 放送地点マスタ > コンテンツ編集画面のヒントは
 *
 *   「SSML 欄は空欄でも構いません。空欄のときは **定型文マスタ** の
 *     粒度別デフォルト文面が使われます (**薄字で表示されている文面**)」
 *
 * と説明しているが、実際の薄字 (placeholder) は
 * `utils/broadcastContents.ts` の **コード内定数** を直接表示していたため、
 * 定型文マスタを編集しても薄字が 1 文字も変わらなかった。
 * その結果「薄字 = 実際に放送される文面」という UI の説明と実挙動が食い違い、
 * 運用者は文面を整備しても効果を確認できなかった。
 *
 * Issue #527 で用意された {@link buildDefaultSsmlResolver} /
 * {@link resolveEffectiveSsml} は **呼び出し元が無く**、
 * `DelayPatternRow` の `defaultSsmlResolver` prop は誰からも渡されていなかった
 * (= 常に `undefined` でコード内既定へフォールバックしていた)。
 * 本コンテキストがその欠けていた配線を担う。
 *
 * ## なぜ props ではなくコンテキストなのか
 *
 * 既定文面を必要とする入力欄は
 *
 *   BroadcastPointsPage → BroadcastPointForm → BroadcastContentsEditor
 *     → ContentPropertyFields → 各 Fields → (DelayPatternRow | LocalizedTextFields)
 *
 * と 6 階層下に散在し、遅延パターン行 / 冒頭文 / 接続線ブロック文の
 * 少なくとも 5 箇所で使う。ハンドラを props でバケツリレーすると中間層に
 * 「使わないのに通すだけ」の props が増え、1 箇所渡し忘れると
 * **その欄だけ薄字がマスタを反映しない** という気づきにくい欠落になる
 * (今回の不具合と同じ壊れ方)。同種の課題を解いている
 * `TranslateContext` / `HomeNavigationGuard` と同じ方式に揃える。
 *
 * Provider が無い環境 (単体テストで個別コンポーネントを描画した場合など) でも
 * 動くよう、デフォルト値は `resolveDefault: undefined` (= コード内既定文面を使う)
 * とする。API 取得に失敗しても薄字が空にならないフェイルセーフでもある。
 *
 * ## ⚠️ 既定文面は「表示専用」であり DB へ書き込まない
 *
 * マスタの文面を入力欄の **値** として焼き込むと、個別文面がマスタより優先される
 * ため「マスタを編集したら空欄コンテンツの放送内容が一括で変わる」
 * (Issue #527 の受け入れ条件) が成立しなくなる。本コンテキストの用途は
 * placeholder (薄字) と多言語欄の既定値表示に限ること。
 */
import { createContext, useContext, useMemo } from 'react';

import type { DefaultSsmlResolver } from '../utils/broadcastContents';

export interface PhraseTemplateContextValue {
  /**
   * 定型文マスタから `(template_id, language_key)` の文面を引く関数。
   *
   * `undefined` のときはマスタ未取得 / 取得失敗を意味し、
   * 各入力欄はコード内既定文面 (従来の挙動) を表示する。
   */
  resolveDefault: DefaultSsmlResolver | undefined;
}

const PhraseTemplateContext = createContext<PhraseTemplateContextValue>({
  resolveDefault: undefined,
});

export function PhraseTemplateProvider({
  resolveDefault,
  children,
}: {
  resolveDefault: DefaultSsmlResolver | undefined;
  children: React.ReactNode;
}): React.JSX.Element {
  const value = useMemo<PhraseTemplateContextValue>(() => ({ resolveDefault }), [resolveDefault]);
  return <PhraseTemplateContext.Provider value={value}>{children}</PhraseTemplateContext.Provider>;
}

/**
 * 入力欄から定型文マスタの既定文面リゾルバを取得する。
 *
 * Provider が無い場合は `undefined` を返すので、呼び出し側は
 * `resolveEffectiveSsml(...)` / `resolveLocalizedDefaults(...)` にそのまま渡せば
 * コード内既定文面へフォールバックする。
 */
export function usePhraseTemplateDefaults(): DefaultSsmlResolver | undefined {
  return useContext(PhraseTemplateContext).resolveDefault;
}
